import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { BriefingTopicBody } from '../types'

export function useBriefingTopics() {
  return useQuery({
    queryKey: ['briefing-topics'],
    queryFn: ApiClient.briefingTopics,
    staleTime: 30 * 60_000
  })
}

// Guest FAQ library (mig 067). Separate query key from the operational topic
// list; create/edit/delete reuse the topic mutations below (which invalidate
// both keys) with isFaq set on the body.
export function useFaqTopics() {
  return useQuery({
    queryKey: ['faq-topics'],
    queryFn: ApiClient.faqTopics,
    staleTime: 30 * 60_000
  })
}

export function useCreateBriefingTopic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BriefingTopicBody) => ApiClient.createBriefingTopic(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing-topics'] })
      qc.invalidateQueries({ queryKey: ['faq-topics'] })
    }
  })
}

export function useUpdateBriefingTopic(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BriefingTopicBody) => ApiClient.updateBriefingTopic(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing-topics'] })
      qc.invalidateQueries({ queryKey: ['faq-topics'] })
      // Per-event briefings may surface stale acks once the topic version
      // bumps; invalidate any cached event briefings.
      qc.invalidateQueries({ queryKey: ['event-briefings'] })
    }
  })
}

export function useDeleteBriefingTopic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteBriefingTopic(id).then(() => id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing-topics'] })
      qc.invalidateQueries({ queryKey: ['faq-topics'] })
    }
  })
}

export function useEventBriefings(eventId: number) {
  return useQuery({
    queryKey: ['event-briefings', eventId],
    queryFn: () => ApiClient.eventBriefings(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    refetchInterval: 60_000     // 1 min - acks come from volunteers
  })
}

export function useAddEventBriefings(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (topicIds: number[]) => ApiClient.addEventBriefings(eventId, topicIds),
    onSuccess: (payload) => qc.setQueryData(['event-briefings', eventId], payload)
  })
}

export function useRemoveEventBriefing(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (topicId: number) =>
      ApiClient.removeEventBriefing(eventId, topicId).then(() => topicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-briefings', eventId] })
  })
}

export function useAckEventBriefing(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (topicId: number) => ApiClient.ackEventBriefing(eventId, topicId),
    onSuccess: (payload) => qc.setQueryData(['event-briefings', eventId], payload)
  })
}

export function useSendBriefingReminder(eventId: number) {
  return useMutation({
    mutationFn: (topicId: number | null) => ApiClient.sendBriefingReminder(eventId, topicId)
  })
}
